// Forex — daily platform FX rates from a public provider (frankfurter.app / ECB) into the
// existing reference.exchange_rate table with company_id=NULL. Tenant overrides still win.
// Slice 4 of api plan/forex.md — the read-only table + "Refresh now" button. History /
// per-currency drill-down fills in later.
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';

export function forexScreen(){
  const rows       = signal([]);         // latest rate per (from, to) pair
  const loading    = signal(true);
  const refreshing = signal(false);
  const refreshMsg = signal('');         // last summary from /refresh (green/red)
  const refreshErr = signal(false);
  const loadErr    = signal('');

  async function load(){
    loading.value = true; loadErr.value = '';
    try {
      const r = await api.get('/admin/fx-rates/latest');
      rows.value = r.data || [];
    } catch(err){
      loadErr.value = (err.data && err.data.error) || err.message || 'Failed to load rates';
      rows.value = [];
    }
    loading.value = false;
  }
  load();

  async function refresh(){
    refreshing.value = true; refreshMsg.value = ''; refreshErr.value = false;
    try {
      const r = await api.post('/admin/fx-rates/refresh', {});
      if(r && r.success){
        const parts = [`Fetched ${r.fetched}`, `Inserted ${r.upserted}`, `Already on file ${r.skipped}`];
        refreshMsg.value = `${parts.join(' · ')} · Rate date ${r.rate_date || '—'} · Source ${r.source}`;
        refreshErr.value = false;
      } else {
        refreshMsg.value = (r && r.error) || 'Refresh failed';
        refreshErr.value = true;
      }
      await load();
    } catch(err){
      refreshMsg.value = (err.data && err.data.error) || err.message || 'Refresh failed';
      refreshErr.value = true;
    }
    refreshing.value = false;
  }

  // Newest rate_date across all rows — the "as of" the table displays.
  const asOf = () => {
    const dates = rows.value.map(r => r.rate_date).filter(Boolean).sort();
    return dates.length ? dates[dates.length - 1] : '—';
  };

  const body = html`
    <div class="card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 class="mb-1">Forex</h2>
            <p class="text-muted mb-0">
              Daily platform exchange rates. Tenant overrides in Company Settings still win.
              As of <b>${() => asOf()}</b>.
            </p>
          </div>
          <button type="button" class="btn btn-primary"
                  ?disabled=${() => refreshing.value}
                  @click=${refresh}>
            ${() => refreshing.value ? 'Refreshing…' : 'Refresh now'}
          </button>
        </div>

        ${() => refreshMsg.value ? html`
          <div class=${'alert py-2 mb-3 ' + (refreshErr.value ? 'alert-danger' : 'alert-success')}>
            ${refreshMsg}
          </div>` : ''}
        ${() => loadErr.value ? html`
          <div class="alert alert-danger py-2 mb-3">${loadErr}</div>` : ''}

        ${() => loading.value
          ? html`<div class="text-muted">Loading rates…</div>`
          : (rows.value.length === 0
              ? html`
                <div class="alert alert-light">
                  <b>No platform rates on file yet.</b> Click <b>Refresh now</b> to pull today's
                  rates from the provider (frankfurter.app / ECB). This will also run daily via
                  the platform cron.
                </div>`
              : html`
                <div class="table-wrap"><table class="cb-table">
                  <thead>
                    <tr>
                      <th>Currency</th>
                      <th>Into</th>
                      <th class="text-right">Rate</th>
                      <th>As of</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${rows.value.map(r => html`
                      <tr>
                        <td class="mono"><b>${r.from_code}</b></td>
                        <td class="mono">${r.to_code}</td>
                        <td class="mono text-right tabular-nums">${Number(r.rate).toFixed(6)}</td>
                        <td class="mono">${r.rate_date}</td>
                        <td><span class="badge badge-light">${r.source}</span></td>
                      </tr>`)}
                  </tbody>
                </table></div>`)}
      </div>
    </div>`;
  return shell('forex', 'Forex', body);
}
